import React, { useState } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import { Search } from '@mui/icons-material';

const ProductSearchApp = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  const apiBaseUrl = 'https://api.prixora.lt/api/FileRetrieval/product';

  const searchProduct = async () => {
    if (!searchTerm.trim()) {
      setProducts([]);
      setError('');
      setHasSearched(false);
      return;
    }

    setLoading(true);
    setError('');
    setHasSearched(true);
    const foundProducts = [];

    try {
      // Search in both email and ftp sources
      const sources = ['email', 'ftp'];

      for (const source of sources) {
        try {
          const response = await fetch(
            `${apiBaseUrl}/${source}/${encodeURIComponent(searchTerm.trim())}`,
            {
              method: 'GET',
              headers: {
                'accept': '*/*'
              }
            }
          );

          if (response.ok) {
            const productData = await response.json();
            // Add source information to the product
            foundProducts.push({
              ...productData,
              source: source
            });
            console.log(`Found product in ${source}:`, productData);
          } else if (response.status === 404) {
            // Product not found in this source, continue with next
            const errorData = await response.json();
            console.log(`Product not found in ${source}:`, errorData.message);
          } else {
            // Other HTTP errors
            throw new Error(`HTTP error! status: ${response.status}`);
          }
        } catch (fetchError) {
          console.warn(`Failed to search in ${source}:`, fetchError);
          // Continue with other sources even if one fails
        }
      }

      setProducts(foundProducts);

      if (foundProducts.length === 0) {
        setError(`Produktas su Tiekėjo Produkto Identifikatoriumi '${searchTerm}' nerastas jokiame šaltinyje.`);
      }

    } catch (err) {
      setError('Nepavyko ieškoti produkto: ' + err.message);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    searchProduct();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      searchProduct();
    }
  };

  const formatPrice = (price, currency) => {
    return `${parseFloat(price).toFixed(2)} ${currency}`;
  };

  const ProductCard = ({ product }) => (
    <Card sx={{ mb: 2, boxShadow: 3 }}>
      <Grid container>
        <Grid item xs={12} md={4}>
          {product.Image && (
            <CardMedia
              component="img"
              height="200"
              image={product.Image}
              alt={product.Item_Name}
              sx={{ objectFit: 'contain', p: 1 }}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          )}
        </Grid>
        <Grid item xs={12} md={8}>
          <CardContent>
            <Typography variant="h6" component="h2" gutterBottom>
              {product.Item_Name}
            </Typography>

            <Box sx={{ mb: 2 }}>
              <Chip
                label={`VPI: ${product.Vendor_Product_Identifier}`}
                color="primary"
                size="small"
                sx={{ mr: 1, mb: 1 }}
              />
              <Chip
                label={product.brand}
                color="secondary"
                size="small"
                sx={{ mr: 1, mb: 1 }}
              />
              <Chip
                label={`Atsargos: ${product.on_stock}`}
                color="success"
                size="small"
                sx={{ mr: 1, mb: 1 }}
              />
              <Chip
                label={`Šaltinis: ${product.source.toUpperCase()}`}
                color="info"
                size="small"
                sx={{ mr: 1, mb: 1 }}
              />
            </Box>

            <Typography variant="h5" color="primary" gutterBottom>
              {formatPrice(product.price, product.currency)}
            </Typography>

            <Typography variant="body2" color="text.secondary" paragraph>
              <strong>Kategorija:</strong> {product.category}
            </Typography>

            <Typography variant="body2" color="text.secondary" paragraph>
              <strong>MPN:</strong> {product.mpn}
            </Typography>

            <Typography variant="body2" color="text.secondary" paragraph>
              <strong>Kilmė:</strong> {product.Country_Of_Origin}
            </Typography>

            <Typography variant="body2" color="text.secondary" paragraph>
              <strong>Vienetas:</strong> {product.unit}
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Svoris:</strong> {product.Brutto_Weight_kg}kg (bruto) / {product.Netto_Weight_kg}kg (neto)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Matmenys:</strong> {product.Brutto_Gross_Depth_mm} × {product.Brutto_Gross_Width_mm} × {product.Brutto_Gross_Height_mm} mm
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Brūkšninis kodas:</strong> {product.Barcode}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Intrastat kodas:</strong> {product.Intrastat_Code}
                </Typography>
              </Grid>
            </Grid>

            {product.Alternative_barcodes && product.Alternative_barcodes.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Alternatyvūs brūkšniniai kodai:</strong>
                </Typography>
                <Box sx={{ mt: 1 }}>
                  {product.Alternative_barcodes.map((barcode, index) => (
                    <Chip
                      key={index}
                      label={barcode}
                      variant="outlined"
                      size="small"
                      sx={{ mr: 1, mb: 1 }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {product.Description_LT && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Aprašymas (LT):</strong> {product.Description_LT}
                </Typography>
              </Box>
            )}

            {product.Name_LT && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Pavadinimas (LT):</strong> {product.Name_LT}
                </Typography>
              </Box>
            )}

            {(product.Image2 || product.Image3) && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Papildomi paveikslėliai:</strong>
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {product.Image2 && (
                    <img
                      src={product.Image2}
                      alt={`${product.Item_Name} - Image 2`}
                      style={{ width: '80px', height: '80px', objectFit: 'contain', border: '1px solid #ddd' }}
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  )}
                  {product.Image3 && (
                    <img
                      src={product.Image3}
                      alt={`${product.Item_Name} - Image 3`}
                      style={{ width: '80px', height: '80px', objectFit: 'contain', border: '1px solid #ddd' }}
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  )}
                </Box>
              </Box>
            )}
          </CardContent>
        </Grid>
      </Grid>
    </Card>
  );

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Produktų Paieškos Sistema
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Ieškokite produktų pagal Tiekėjo Produkto Identifikatorių naudodami Prixora API
          </Typography>
          <Button
            variant="outlined"
            color="secondary"
            href="/exclude"
            sx={{ mt: 2 }}
          >
            Pašalinti puslapį
          </Button>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Įveskite Tiekėjo Produkto Identifikatorių (pvz., 000051004009)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={handleKeyPress}
            InputProps={{
              startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />
          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={loading}
            sx={{ minWidth: 120 }}
          >
            {loading ? <CircularProgress size={24} /> : 'Ieškoti'}
          </Button>
        </Box>

        {error && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {loading
              ? 'Ieškoma el. pašto ir ftp šaltiniuose...'
              : products.length > 0
                ? `Rasta ${products.length} produktų skirtinguose šaltiniuose`
                : 'Įveskite Tiekėjo Produkto Identifikatorių paieškai'
            }
          </Typography>
        </Box>
      </Paper>

      {products.length > 0 && (
        <Box>
          <Typography variant="h5" component="h2" gutterBottom>
            Paieškos Rezultatai (rasta {products.length})
          </Typography>
          {products.map((product, index) => (
            <ProductCard key={`${product.source}-${index}`} product={product} />
          ))}
        </Box>
      )}

      {hasSearched && searchTerm && products.length === 0 && !loading && !error && (
        <Box elevation={1} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            Produktų nerasta "{searchTerm}"
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Pabandykite ieškoti su kitu Tiekėjo Produkto Identifikatoriumi
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default ProductSearchApp;